import React, { useState } from "react";
import "../../../../styles/gridStyle.css";
import { IFileProcessModel } from "../../../../typings/models/FileProcessModel";
import FileProcessGridRow from "./FileProcessGridRow";
import Loader from "../../../../components/molecules/Loader";

interface GridProps {
  data: IFileProcessModel[];
  isLoading: boolean;
}

const FileProcessGrid: React.FC<GridProps> = ({ data, isLoading }) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const itemsPerPage = 10;
  const maxPagesToShow = 5;

  // Filter the data based on the search query
  const filteredData =
    data.length > 0
      ? data.filter((item) =>
          Object.values(item).some((value) =>
            value?.toString().toLowerCase().includes(searchQuery?.toLowerCase())
          )
        )
      : [];

  // Function to perform sorting based on the current sort settings
  const sortedData = filteredData.sort((a, b) => {
    if (sortColumn === null) return 0;

    const aValue = a[sortColumn];
    const bValue = b[sortColumn];

    if (aValue === null && bValue === null) return 0;
    if (aValue === null) return sortDirection === "asc" ? 1 : -1;
    if (bValue === null) return sortDirection === "asc" ? -1 : 1;

    if (aValue < bValue) {
      return sortDirection === "asc" ? -1 : 1;
    } else if (aValue > bValue) {
      return sortDirection === "asc" ? 1 : -1;
    } else {
      return 0;
    }
  });

  // Calculate the total number of pages
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  // Get the data to display on the current page
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const dataToShow = sortedData.slice(startIndex, endIndex);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to the first page when searching
  };

  const handlePagination = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleSort = (column: string) => {
    if (column === sortColumn) {
      // If the same column is clicked again, toggle the sort direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // If a new column is clicked, set the new column and default to ascending sort
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const renderPageButtons = () => {
    const pageButtons = [];
    let startPage = 1;
    let endPage = totalPages;

    if (totalPages > maxPagesToShow) {
      const halfMaxPages = Math.floor(maxPagesToShow / 2);
      startPage = Math.max(currentPage - halfMaxPages, 1);
      endPage = startPage + maxPagesToShow - 1;

      if (endPage > totalPages) {
        endPage = totalPages;
        startPage = endPage - maxPagesToShow + 1;
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePagination(i)}>
            {i}
          </button>
        </li>
      );
    }

    if (totalPages > maxPagesToShow && endPage < totalPages) {
      pageButtons.push(
        <li key="separator" className="page-item disabled">
          <button className="page-link">...</button>
        </li>
      );
      pageButtons.push(
        <li
          key={totalPages}
          className={`page-item ${currentPage === totalPages ? "active" : ""}`}
        >
          <button
            className="page-link"
            onClick={() => handlePagination(totalPages)}
          >
            {totalPages}
          </button>
        </li>
      );
    }

    return pageButtons;
  };
  return (
    <div className="container">
      <input
        type="text"
        placeholder="Search..."
        value={searchQuery}
        onChange={handleSearch}
        className="form-control mb-3"
      />

      <table
        className="table table-bordered table-hover csp-table-scroll"
      >
        {/* Table header */}
        <thead>
          <tr>
            <th className="grid-header" onClick={() => handleSort("fileName")}>
              File Name{" "}
              {sortColumn === "fileName" && (
                <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
              )}
            </th>
            <th className="grid-header" onClick={() => handleSort("status")}>
              Status{" "}
              {sortColumn === "status" && (
                <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
              )}
            </th>
            <th
              className="grid-header"
              onClick={() => handleSort("uploadedDate")}
            >
              Submitted Date{" "}
              {sortColumn === "uploadedDate" && (
                <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
              )}
            </th>
            <th className="grid-header" onClick={() => handleSort("success")}>
              Success Count{" "}
              {sortColumn === "success" && (
                <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
              )}
            </th>
            <th className="grid-header" onClick={() => handleSort("error")}>
              Error Count{" "}
              {sortColumn === "error" && (
                <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
              )}
            </th>
            <th
              className="grid-header"
              onClick={() => handleSort("errorMessage")}
            >
              Remarks{" "}
              {sortColumn === "errorMessage" && (
                <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
              )}
            </th>
            {/* Add other header columns here */}
          </tr>
        </thead>
        <tbody>
          {dataToShow.map((item) => (
            <FileProcessGridRow key={item.fileProcessId} data={item} />
          ))}
        </tbody>
      </table>
      {isLoading && <Loader />}

      {/* Pagination */}
      <nav aria-label="Page navigation example">
        <ul className="pagination">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => handlePagination(currentPage - 1)}
            >
              Previous
            </button>
          </li>
          {renderPageButtons()}
          <li
            className={`page-item ${
              currentPage === totalPages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() => handlePagination(currentPage + 1)}
            >
              Next
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
};

export default FileProcessGrid;
